import React, { useState, useEffect, useRef, useContext, memo, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, useUpdateUser, useUser } from "../../Context/UserContext";
import Heart from "../Svgs/Heart";
import Trash from "../Svgs/Trash";
const FirstSOVFullImage = "/images/imagePlaceholder.webp";

// 🔧 Clean and normalize area strings
const cleanAreaValue = (value) => {
  if (!value) return "N/A";
  const numberMatch = value.match(/[\d,.]+/);
  if (!numberMatch) return value;
  const number = parseFloat(numberMatch[0].replace(/,/g, ""));
  return isNaN(number) ? value : `${number} m²`;
};

// 🔧 Helper function to test if an image URL is working
const testImageUrl = (url) => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
    
    // Timeout after 5 seconds
    setTimeout(() => resolve(false), 5000);
  });
};

// 🔧 Function to find the first working image from an array
const findWorkingImage = async (imageArray, fallbackImage) => {
  if (!imageArray || !Array.isArray(imageArray) || imageArray.length === 0) {
    return fallbackImage;
  }

  // Test each image in the array
  for (const imageUrl of imageArray) {
    const isWorking = await testImageUrl(imageUrl);
    if (isWorking) {
      return imageUrl;
    }
  }

  // If no images work, return fallback
  return fallbackImage;
};

const AkiyaCard = memo(({ item, isFavorite = false, onDelete }) => {
  const { Images, Address, Area_of_House, Area_of_Land, Price, property_id, Empty_or_Not } = item;

  const navigate = useNavigate();
  const user = useUser();
  const UpdateUser = useUpdateUser();
  const { setShowModal } = useContext(UserContext);
  const [imageSrc, setImageSrc] = useState(FirstSOVFullImage); // Start with fallback
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageTestComplete, setImageTestComplete] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const imgRef = useRef(null);
  const [isBuying, setIsBuying] = useState(true);

  const isRentalPrice = (priceString) => {
    if (!priceString || typeof priceString !== 'string') return false;
    const lowerPrice = priceString.toLowerCase();
    const rentalKeywords = ['month', 'week', 'day', 'rental', 'rent', 'per', '月', '週', '日'];
    return rentalKeywords.some(keyword => lowerPrice.includes(keyword));
  };

  useEffect(() => {
    const parsePrice = (priceString) => {
      if (!priceString || typeof priceString !== 'string') return 0;
      let cleanedPrice = priceString.toLowerCase();
      if (cleanedPrice.includes('million') || cleanedPrice.includes('百万')) {
        cleanedPrice = cleanedPrice.replace(/million|百万/gi, '');
        return parseFloat(cleanedPrice.replace(/[^0-9.]/g, '')) * 1_000_000;
      }
      if (cleanedPrice.includes('billion') || cleanedPrice.includes('十億')) {
        cleanedPrice = cleanedPrice.replace(/billion|十億/gi, '');
        return parseFloat(cleanedPrice.replace(/[^0-9.]/g, '')) * 1_000_000_000;
      }
      return parseFloat(cleanedPrice.replace(/[^0-9.]/g, ''));
    };

    let buyingProperty = true;
    const valueToCheck = typeof Empty_or_Not === "string" ? Empty_or_Not.toLowerCase() : "";
    const buyingKeywords = ["akiya", "buy", "sale", "purchase"];
    const rentalKeywords = ["rent", "rental", "lease"];
    const isRental = rentalKeywords.some(keyword => valueToCheck.includes(keyword));
    const isBuyingValue = buyingKeywords.some(keyword => valueToCheck.includes(keyword));

    if (isBuyingValue) {
      buyingProperty = true;
    } else if (isRental) {
      buyingProperty = false;
    }

    if (Price) {
      const priceValue = parsePrice(Price);
      const rentalBased = isRentalPrice(Price);
      if (priceValue > 300000 && !rentalBased) {
        buyingProperty = true;
      } else if (rentalBased) {
        buyingProperty = false;
      }
    }

    setIsBuying(buyingProperty);
  }, [Empty_or_Not, Price]);

  // 🔧 Enhanced image loading with fallback testing
  useEffect(() => {
    const loadBestImage = async () => {
      try {
        const workingImage = await findWorkingImage(Images, FirstSOVFullImage);
        setImageSrc(workingImage);
        setImageTestComplete(true);
        
        // Log for debugging
        if (Images && Images.length > 0) {
          console.log(`Property ${property_id}: Testing images...`);
          console.log(`First image: ${Images[0]}`);
          console.log(`Selected working image: ${workingImage}`);
          
          if (workingImage === FirstSOVFullImage) {
            console.log(`⚠️ Property ${property_id}: No working images found, using fallback`);
          } else {
            console.log(`✅ Property ${property_id}: Found working image`);
          }
        }
      } catch (error) {
        console.error(`Error testing images for property ${property_id}:`, error);
        setImageSrc(FirstSOVFullImage);
        setImageTestComplete(true);
      }
    };

    loadBestImage();
  }, [Images, property_id]);

  // Memoized calculations for better performance
  const themeColors = useMemo(() => ({
    primary: isBuying ? "#10c9c0" : "#3B82F6",
    hover: isBuying ? "#0eb1a9" : "#2563EB"
  }), [isBuying]);

  const formattedData = useMemo(() => ({
    price: (() => {
      const cleanedPrice = Price ? Price.replace(/[^\d]/g, "") : "";
      return cleanedPrice && cleanedPrice.length > 4 && Number(cleanedPrice) >= 50000
        ? `¥ ${Number(cleanedPrice).toLocaleString()}`
        : Price && Price.toLowerCase().includes('million')
        ? `¥ ${Price}`
        : "Consult with administrator";
    })(),
    areaOfLand: cleanAreaValue(Area_of_Land),
    areaOfHouse: cleanAreaValue(Area_of_House)
  }), [Price, Area_of_Land, Area_of_House]);

  const isFavoritePresent = useMemo(() => 
    user && user.favorite && user.favorite.includes(property_id), 
    [user, property_id]
  );

  // Optimized image loading with intersection observer
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && imageTestComplete) {
            setImageLoaded(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current && imageTestComplete) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [imageTestComplete]);

  // Memoized event handlers
  const handleToggleFavorite = useCallback((event) => {
    event.stopPropagation();
    if (!user) {
      setShowModal(true);
    } else {
      const favoriteData = isFavoritePresent
        ? user.favorite.filter((f) => f !== property_id)
        : [...(user.favorite || []), property_id];
      UpdateUser({ ...user, favorite: favoriteData });
    }
  }, [user, isFavoritePresent, property_id, setShowModal, UpdateUser]);

  const handleCardClick = useCallback(() => {
    navigate(`/individual-akiya/${property_id}`);
  }, [navigate, property_id]);

  const handleDelete = useCallback((event) => {
    event.stopPropagation();
    if (onDelete) onDelete(property_id);
  }, [onDelete, property_id]);

  // 🔧 Enhanced image error handler with additional fallback
  const handleImageError = useCallback((e) => {
    console.log(`❌ Image failed to load for property ${property_id}: ${e.target.src}`);
    
    // If the current src is not the fallback, try the fallback
    if (e.target.src !== FirstSOVFullImage) {
      e.target.src = FirstSOVFullImage;
    } else {
      // If even fallback fails, we could set a different placeholder or handle differently
      console.error(`❌ Even fallback image failed for property ${property_id}`);
    }
  }, [property_id]);

  return (
    <div
      className="group cursor-pointer h-full"
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <article className="bg-white rounded-xl overflow-hidden shadow-[rgba(0,0,0,0.08)_0px_4px_20px_0px] hover:shadow-[rgba(0,0,0,0.25)_0px_20px_40px_0px] transition-all duration-500 ease-out transform sm:hover:scale-[1.02] sm:hover:-translate-y-2 h-[32rem] relative">
        
        {/* Enhanced Image Section */}
        <div className="h-[40%] w-full relative overflow-hidden">
          <div className="relative h-full w-full">
            <img
              loading="lazy"
              ref={imgRef}
              className={`h-full w-full object-cover transition-transform duration-700 ease-out will-change-transform ${
                isHovered 
                  ? 'sm:scale-110' 
                  : 'scale-100'
              }`}
              src={imageSrc}
              alt="Akiya 2.0"
              decoding="async"
              onError={handleImageError}
            />
            
            {/* Elegant Loading Component */}
            {(!imageLoaded || !imageTestComplete) && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                {/* Animated Loading Circles */}
                <div className="relative">
                  {/* Outer rotating ring */}
                  <div className="w-16 h-16 border-4 border-gray-200 rounded-full animate-spin">
                    <div className="absolute top-0 left-0 w-4 h-4 bg-gradient-to-r from-blue-500 to-teal-400 rounded-full transform -translate-x-1 -translate-y-1"></div>
                  </div>
                  
                  {/* Inner pulsing dot */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 bg-gradient-to-r from-teal-400 to-blue-500 rounded-full animate-pulse"></div>
                  </div>
                  
                  {/* Floating dots around */}
                  <div className="absolute -inset-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full absolute top-0 left-1/2 transform -translate-x-1/2 animate-bounce" style={{animationDelay: '0s'}}></div>
                    <div className="w-2 h-2 bg-teal-400 rounded-full absolute top-1/2 right-0 transform -translate-y-1/2 animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full absolute bottom-0 left-1/2 transform -translate-x-1/2 animate-bounce" style={{animationDelay: '0.4s'}}></div>
                    <div className="w-2 h-2 bg-teal-400 rounded-full absolute top-1/2 left-0 transform -translate-y-1/2 animate-bounce" style={{animationDelay: '0.6s'}}></div>
                  </div>
                </div>
                
                {/* Loading text with typewriter effect */}
                {!imageTestComplete && (
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
                    <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" style={{animationDelay: '0s'}}></div>
                        <div className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700 animate-pulse">
                        Finding best image
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Subtle animated background pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12 animate-[slide_2s_ease-in-out_infinite]"></div>
                </div>
              </div>
            )}
            
            {/* Hover overlay */}
            <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-all duration-500 ${
              isHovered ? 'opacity-100' : 'opacity-80'
            }`}></div>
            
            {/* Heart/Favorite Button */}
            {!isFavorite && (
              <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
                <button
                  onClick={handleToggleFavorite}
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full backdrop-blur-sm transition-all duration-300 flex items-center justify-center ${
                    isFavoritePresent
                      ? 'bg-white/90 text-red-500'
                      : 'bg-black/20 text-white hover:bg-white/90 hover:text-red-500'
                  } ${isHovered ? 'scale-110' : 'scale-100'}`}
                >
                  <div className="w-5 h-5 sm:w-6 sm:h-6">
                    <Heart />
                  </div>
                </button>
              </div>
            )}

            {/* Enhanced Price Badge */}
            <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 sm:p-4 transition-all duration-500 ${
              isHovered ? 'from-black/90' : 'from-black/80'
            }`}>
              <div className="text-white text-center">
                <div className="text-lg sm:text-xl font-bold">
                  {formattedData.price}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Content Section */}
        <div className="flex flex-col h-[60%] p-3 sm:p-4 space-y-3 sm:space-y-4 relative">
          {/* Address */}
          <div className={`transition-colors duration-500 ${
            isHovered ? 'text-gray-800' : 'text-gray-900'
          }`}>
            <p className="text-gray-600 text-xs sm:text-sm font-medium mb-1">Address:</p>
            {Address && (
              <p className="text-gray-900 text-sm sm:text-base leading-tight line-clamp-2">
                {Address.slice(0, 90)}
              </p>
            )}
          </div>
          
          {/* Property Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 flex-1">
            <div className="transition-colors duration-500">
              <p className="text-gray-600 text-xs sm:text-sm font-medium mb-1">Land Area:</p>
              <p className="text-gray-900 text-sm sm:text-base font-semibold">{formattedData.areaOfLand}</p>
            </div>
            
            <div className="transition-colors duration-500">
              <p className="text-gray-600 text-xs sm:text-sm font-medium mb-1">House Area:</p>
              <p className="text-gray-900 text-sm sm:text-base font-semibold">{formattedData.areaOfHouse}</p>
            </div>
          </div>

          {/* Enhanced Action Button */}
          <div className="mt-auto">
            <div
              className={`w-full text-center py-2 sm:py-3 rounded-lg text-white font-semibold text-sm sm:text-base transition-all duration-300 ${
                isHovered 
                  ? 'transform scale-105 shadow-lg' 
                  : 'transform scale-100'
              }`}
              style={{ 
                backgroundColor: isHovered ? themeColors.hover : themeColors.primary,
                boxShadow: isHovered ? `0 8px 25px ${themeColors.primary}40` : 'none'
              }}
            >
              {isBuying ? "For Sale" : "Rental"}
            </div>
          </div>

          {/* Delete Button for Favorites */}
          {isFavorite && (
            <button
              className={`absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-red-100 hover:bg-red-200 rounded-lg sm:rounded-xl p-2 transition-all duration-300 ${
                isHovered ? 'scale-110' : 'scale-100'
              }`}
              onClick={handleDelete}
            >
              <div className="w-5 h-5 sm:w-6 sm:h-6">
                <Trash />
              </div>
            </button>
          )}
        </div>
        
        {/* Theme-colored accent border */}
        <div 
          className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-500 ${
            isHovered ? 'w-2' : 'w-1'
          }`}
          style={{ backgroundColor: themeColors.primary }}
        ></div>
        
        {/* Subtle border accent */}
        <div 
          className={`absolute inset-0 rounded-xl border-2 transition-all duration-500 pointer-events-none ${
            isHovered ? 'border-opacity-30' : 'border-transparent'
          }`}
          style={{ borderColor: isHovered ? themeColors.primary : 'transparent' }}
        ></div>
      </article>
      
      {/* Enhanced Custom CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        @keyframes slide {
          0% {
            transform: translateX(-100%) skewX(-12deg);
          }
          100% {
            transform: translateX(300%) skewX(-12deg);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(180deg);
          }
        }
        
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 5px rgba(59, 130, 246, 0.5);
          }
          50% {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 30px rgba(16, 201, 192, 0.6);
          }
        }
      `}</style>
    </div>
  );
});

export default AkiyaCard;